import React, { useCallback, useMemo, useEffect } from "react";
import { Controller, useWatch } from "react-hook-form";
import FloatingInput from "../../../components/input/FloatingInput";
import SetCountyModal from "../popups/SetCountyModal";
import { populateAddressFields } from "../seller/sellerNet.shared";

/**
 * AddressTab Component
 * ===================
 *
 * Main address search interface with autocomplete and manual entry.
 * Handles address selection from Google Places API and manual form filling.
 *
 * Flow:
 * 1. User types address in search input
 * 2. Google Places API provides autocomplete suggestions
 * 3. User selects suggestion OR fills manual fields
 * 4. Address data is validated and form is populated
 * 5. Manual edit form becomes visible for corrections
 *
 * States:
 * - Search Phase: Autocomplete suggestions shown
 * - Edit Phase: Manual form fields shown (suggestionSelected = true)
 *
 * @param {Object} control - React Hook Form control
 * @param {Function} setValue - React Hook Form setValue function
 * @param {Function} getValues - React Hook Form getValues function
 * @param {Object} selectedAddressData - Address data from autocomplete/parcel selection
 * @param {string} query - Current search query
 * @param {Function} onChange - Search input change handler
 * @param {Function} onBlur - Search input blur handler
 * @param {Function} onKeyDown - Search input key handler
 * @param {Array} suggestions - Google Places suggestions
 * @param {number} activeIndex - Active suggestion index
 * @param {Function} setActiveIndex - Set active suggestion
 * @param {Function} selectSuggestion - Handle suggestion selection
 * @param {Array} suggestionItemRefs - Refs for suggestion items
 * @param {string} siteUnit - Unit number value
 * @param {Function} handleSiteUnitInputChange - Unit input handler
 * @param {Function} handleAddressSearch - Manual address search
 * @param {Object} addressState - Address search state
 * @param {Function} openModal - Modal opener
 * @param {Function} closeModal - Modal closer
 * @param {boolean} suggestionSelected - Whether manual edit form is shown
 * @param {Function} setSuggestionSelected - Toggle manual edit form
 * @param {string} [mode="seller"] - Calculator mode ("buyer" or "seller")
 */
const AddressTab = React.memo(
  ({
    control,
    setValue,
    getValues,
    selectedAddressData,
    query,
    onChange,
    onBlur,
    onKeyDown,
    suggestions,
    activeIndex,
    setActiveIndex,
    selectSuggestion,
    suggestionItemRefs,
    siteUnit,
    handleSiteUnitInputChange,
    handleAddressSearch,
    addressState,
    openModal,
    closeModal,
    suggestionSelected,
    setSuggestionSelected,
    mode = "seller",
  }) => {
    // Watch address fields so inputs update when setValue is called (e.g. from selectedAddressData)
    const watchedCounty = useWatch({
      control,
      name: "addressMeta.closing_county",
    });
    const watchedStreetNo = useWatch({
      control,
      name: "addressMeta.street_no",
    });
    const watchedStreetName = useWatch({
      control,
      name: "addressMeta.street_name",
    });
    const watchedCity = useWatch({ control, name: "addressMeta.city" });
    const watchedState = useWatch({ control, name: "addressMeta.state" });
    const watchedZip = useWatch({ control, name: "addressMeta.zip" });
    void useWatch({ control, name: "addressMeta.unitNumber" }); // watched for reactivity

    // Check if address has been selected from suggestions to show search button
    const shouldShowSearchButton = useMemo(() => {
      return !!selectedAddressData;
    }, [selectedAddressData]);

    // Normalize address data (support both street_no/street_name and streetNumber/streetName)
    const normalizedAddress = useMemo(() => {
      //  console.log('🔄 [AddressTab] normalizedAddress useMemo - selectedAddressData:', selectedAddressData);
      if (!selectedAddressData) {
        //  console.log('⚠️ [AddressTab] normalizedAddress returning null - no selectedAddressData');
        return null;
      }
      const result = {
        address: selectedAddressData.address ?? "",
        addressIncludesUnit: selectedAddressData.addressIncludesUnit ?? false,
        street_no:
          selectedAddressData.street_no ??
          selectedAddressData.streetNumber ??
          "",
        street_name:
          selectedAddressData.street_name ??
          selectedAddressData.streetName ??
          "",
        city: selectedAddressData.city ?? "",
        state: selectedAddressData.state ?? "",
        zip: selectedAddressData.zip ?? "",
        unitNumber: selectedAddressData.unitNumber ?? "",
        formattedAddress: selectedAddressData.formattedAddress ?? "",
        closing_county: selectedAddressData.closing_county ?? "",
      };
      //  console.log('✅ [AddressTab] normalizedAddress created:', result);
      //  console.log('🎯 [AddressTab] address in normalizedAddress:', result.address, 'addressIncludesUnit:', result.addressIncludesUnit);
      return result;
    }, [selectedAddressData]);

    // Update form fields when address is selected from autocomplete or parcel
    useEffect(() => {
      //  console.log('🔄 [AddressTab] useEffect triggered - normalizedAddress:', !!normalizedAddress);

      if (normalizedAddress && setValue && typeof setValue === "function") {
        //  console.log('📝 [AddressTab] Setting form fields with normalizedAddress:', normalizedAddress);

        const success = populateAddressFields(
          normalizedAddress,
          setValue,
          mode,
        );

        if (success) {
          setSuggestionSelected(true);
          //  console.log('✅ [AddressTab] Form fields set and suggestionSelected set to true');
        }
      } else {
        //  console.log('⚠️ [AddressTab] useEffect skipped - normalizedAddress:', !!normalizedAddress, 'setValue:', !!setValue);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- mode intentionally excluded
    }, [normalizedAddress, setValue, setSuggestionSelected]);

    // Note: Unit checkbox is now handled in the main form population useEffect above

    const handleSuggestionClick = useCallback(
      (suggestion, index) => {
        selectSuggestion(suggestion);
      },
      [selectSuggestion],
    );

    const handleSuggestionMouseEnter = useCallback(
      (index) => {
        setActiveIndex(index);
      },
      [setActiveIndex],
    );

    // on button Search Address
    const handleAddressSearchLocal = useCallback(() => {
      //  console.log('🔍 [AddressTab] handleAddressSearchLocal Manual address search initiated');

      // Get form values using control
      const formValues = control._formValues || {};
      //  console.log('📝 [AddressTab] Current form values:', formValues);

      const addressData = {
        street_no: formValues.addressMeta?.street_no || "",
        street_name: formValues.addressMeta?.street_name || "",
        unitNumber: formValues.addressMeta?.unitNumber || "",
        closing_county: formValues.addressMeta?.closing_county || "",
        city: formValues.addressMeta?.city || "",
        state: formValues.addressMeta?.state || "",
        zip: formValues.addressMeta?.zip || "",
      };
      //  console.log('📤 [AddressTab] Address data for search:', addressData);

      handleAddressSearch(addressData);
      //  console.log('✅ [AddressTab] Address search handler called');
    }, [control, handleAddressSearch]);

    return (
      <>
        {/* Address Search Input */}
        <div className="frm-field-1">
          <div className="form-group">
            <FloatingInput
              type="search"
              name="search"
              placeholder="What is the Property Address?"
              className="inpt-pla ic-search"
              value={query}
              onChange={onChange}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
            />
          </div>

          {/* Address Suggestions */}
          {suggestions.length > 0 && (
            <div className="search-list-prop">
              {suggestions.map((s, idx) => (
                <div
                  key={s.place_id || idx}
                  ref={(el) => (suggestionItemRefs.current[idx] = el)}
                  className={`search-list-prop-wrap ${idx === activeIndex ? "active" : ""}`}
                  onMouseEnter={() => handleSuggestionMouseEnter(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSuggestionClick(s, idx);
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    handleSuggestionClick(s, idx);
                  }}
                >
                  <div className="search-list-prop-heading">
                    {s.description || s.structured_formatting?.main_text}
                  </div>
                  <div className="search-list-prop-desc">
                    {s.structured_formatting?.secondary_text || ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div class={`editManually ${suggestionSelected ? "show" : ""}`}>
          {/* Street Number, Street Name */}
          <div className="frm-field-2">
            <div className="form-group input-sticky">
              <Controller
                name="addressMeta.street_no"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <FloatingInput
                    {...field}
                    value={
                      (watchedStreetNo !== undefined
                        ? watchedStreetNo
                        : field.value) ?? ""
                    }
                    placeholder=""
                    label="Street Number"
                    type="text"
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            </div>

            <div className="form-group input-sticky">
              <Controller
                name="addressMeta.street_name"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <FloatingInput
                    {...field}
                    value={
                      (watchedStreetName !== undefined
                        ? watchedStreetName
                        : field.value) ?? ""
                    }
                    placeholder=""
                    label="Street Name"
                    type="text"
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            </div>

            {/* Address Includes Unit Checkbox */}
            <div className="form-group">
              <div className=" checkbox-fld ad-val">
                <div className="unit-div">
                  <Controller
                    name="showSiteUnit"
                    control={control}
                    render={({ field }) => (
                      <>
                        <p className="unit-toggle switchtoggle">
                          <input
                            type="checkbox"
                            id="unitToggle"
                            checked={!!field.value}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              field.onChange(checked);
                              setValue &&
                                setValue(
                                  "addressMeta.addressIncludesUnit",
                                  checked,
                                );
                            }}
                          />
                          <label htmlFor="unitToggle">Unit #</label>
                        </p>

                        <div>
                          <div className="">
                            <div className="form-group input-sticky">
                              <Controller
                                name="addressMeta.unitNumber"
                                control={control}
                                render={({ field: unitField }) => {
                                  //    console.log('🎯 [AddressTab] Unit input render - watchedUnitNumber:', watchedUnitNumber, 'unitField.value:', unitField.value, 'field.value (toggle):', field.value);
                                  return (
                                    <FloatingInput
                                      {...unitField}
                                      value={unitField.value ?? ""}
                                      label="Unit No."
                                      type="text"
                                      disabled={!field.value}
                                      className={
                                        !field.value ? "disabled-input" : ""
                                      }
                                      onChange={(e) => {
                                        unitField.onChange(e.target.value);
                                        handleSiteUnitInputChange(
                                          e.target.value,
                                        );
                                      }}
                                    />
                                  );
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* City, State, ZIP */}
          <div className="frm-field-2">
            <div className="form-group input-sticky">
              <Controller
                name="addressMeta.city"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <FloatingInput
                    {...field}
                    value={
                      (watchedCity !== undefined ? watchedCity : field.value) ??
                      ""
                    }
                    placeholder=""
                    label="City"
                    type="text"
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            </div>
            <div className="form-group input-sticky">
              <Controller
                name="addressMeta.state"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <FloatingInput
                    {...field}
                    value={
                      (watchedState !== undefined
                        ? watchedState
                        : field.value) ?? ""
                    }
                    placeholder=""
                    label="State"
                    type="text"
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            </div>
            <div className="form-group input-sticky">
              <Controller
                name="addressMeta.zip"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <FloatingInput
                    {...field}
                    value={
                      (watchedZip !== undefined ? watchedZip : field.value) ??
                      ""
                    }
                    placeholder=""
                    label="Zip"
                    type="text"
                    maxLength={5}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      field.onChange(val);
                    }}
                    onBlur={(e) => {
                      try {
                        field.onBlur && field.onBlur(e);
                      } catch (err) {
                        /* ignore */
                      }
                    }}
                  />
                )}
              />
            </div>
          </div>

          <div className="frm-field-2">
            {/* Closing County */}
            <div className="calc-frm-fld form-group  input-sticky">
              <Controller
                name="addressMeta.closing_county"
                control={control}
                defaultValue={
                  getValues
                    ? (getValues("addressMeta.closing_county") ?? "")
                    : ""
                }
                render={({ field }) => (
                  <FloatingInput
                    {...field}
                    value={
                      typeof watchedCounty !== "undefined"
                        ? watchedCounty
                        : field.value
                    }
                    placeholder=""
                    label="Closing County"
                    type="text"
                  />
                )}
              />
            </div>
            <div className="calc-frm-fld form-group conty-full align-middle1">
              <button
                className="submit-btn btn-bg inpt-btn sht-btn btn-add-more heading-b"
                onClick={(e) => {
                  e.preventDefault();
                  openModal({
                    title: "Set Closing County",
                    size: "smallmodel",
                    content: (
                      <SetCountyModal
                        control={control}
                        setValue={setValue}
                        onClose={closeModal}
                        calc={mode}
                        getValues={getValues}
                      />
                    ),
                  });
                }}
              >
                Set County
              </button>
            </div>
            <div className="form-group"></div>
          </div>

          <div className="frm-field-1 rightflxbtn align-middle1">
            {/* Address Search Button */}
            {shouldShowSearchButton && (
              <div className="form-group text-center slide-down">
                <p style={{ fontSize: "12px" }}>
                  If no match found, search manually.
                </p>
                <button
                  type="button"
                  className="submit-btn btn-bg inpt-btn sht-btn ch-btnsave-new"
                  onClick={handleAddressSearchLocal}
                  disabled={addressState?.isSearching}
                >
                  {addressState?.isSearching
                    ? "Searching..."
                    : "Search Address"}
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  },
);
/*
test
*/
AddressTab.displayName = "AddressTab";

export default AddressTab;
